import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {UserApi} from "components/src/api/admin/generated";
import {DialogCloseCause} from "components/src/common/Enums";
import {RenderAlert} from "components/src/common/RenderAlert";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {logger} from "components/src/utils/Logger";
import {StringUtils} from "components/src/utils/StringUtils";
import {Utils} from "components/src/utils/Utils";
import {useRef, useState} from 'react'
import Button from 'react-bootstrap/Button';
import FloatingLabel from 'react-bootstrap/FloatingLabel';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import {useNavigate} from "react-router-dom";

/*
 * REST api for accessing the back-end
 */
const userApi = new UserApi(ApiConfig.config());

/**
 * The OnClose event should be handled by the owner. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause : DialogCloseCause) => void;

interface SendOTPSecretByEmailPopupProps {
    onClose : OnClose
}

export const SendOTPSecretByEmailPopup = ({onClose} : SendOTPSecretByEmailPopupProps ) =>
{
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * The recipient
     */
    const [recipient, setRecipient] = useState<string>("");
    /**
     * The from header
     */
    const [from, setFrom] = useState<string>("");
    /**
     * The envelope sender
     */
    const [sender, setSender] = useState<string>("");
    /**
     * The phone number
     */
    const [phoneNumber, setPhoneNumber] = useState<string>("");
    /**
     * The encryption password
     */
    const [password, setPassword] = useState<string>("");
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * Set to true while sending to show a spinner on the download button
     */
    const [sending, setSending] = useState<boolean>(false);
    /**
     * Set to true if the message was sent
     */
    const [sent, setSent] = useState<boolean>(false);
    /**
     * Ref to the recipient input field
     */
    const recipientInputRef = useRef<HTMLInputElement>(null);
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    // called when the dialog is closed without clicking export
    const onHideDialog = () => {
        onClose(DialogCloseCause.Cancel);
    }

    const send = () =>
    {
        // reset error message
        setErrorMessage(undefined);

        setSent(false);
        setSending(true);

        userApi.userControllerSendOTPSecretByEmail(recipient, from, sender, phoneNumber, true, password).then(() => {
            setSent(true);
        })
        .catch((err: Error | AxiosError) => {
            logger.error("Error sending email", err);
            Utils.redirectToLoginIfNotAuthenticated(err, navigate);
            setErrorMessage(Utils.getErrorMessage(err, t));
        })
        .finally(() => {
            setSending(false);
        });
    }

    return (
        <Modal show={true} onHide={() => onHideDialog()}
               onEntered={() => recipientInputRef.current?.focus()}
               size="xl"
               fullscreen="lg-down"
               backdrop="static"
        >
            <Modal.Header closeButton>
                <Modal.Title>{t("send-otp-secret-by-email.title")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="row flex-grow-1">
                    <main className="col d-flex flex-column overflow-auto modal-main-body">
                        <RenderAlert message={errorMessage} setMessage={setErrorMessage}
                                     tooltipPlacement={"bottom"}/>

                        {sent &&
                            <RenderAlert message={t('send-otp-secret-by-email.email-sent')} variant="success"/>
                        }

                        <p>
                            {t('send-otp-secret-by-email.intro')}
                        </p>

                        <FloatingLabel label={t("send-otp-secret-by-email.recipient")} className="text-secondary mb-3">
                            <Form.Control type="email" value={recipient} placeholder={t("send-otp-secret-by-email.recipient")}
                                          ref={recipientInputRef}
                                          onChange={e => setRecipient(e.target.value)}/>
                        </FloatingLabel>

                        <FloatingLabel label={t("send-otp-secret-by-email.from")} className="text-secondary mb-3">
                            <Form.Control type="email" value={from} placeholder={t("send-otp-secret-by-email.from")}
                                          onChange={e => setFrom(e.target.value)}/>
                        </FloatingLabel>

                        <FloatingLabel label={t("send-otp-secret-by-email.sender")} className="text-secondary mb-3">
                            <Form.Control type="email" value={sender} placeholder={t("send-otp-secret-by-email.sender")}
                                          onChange={e => setSender(e.target.value)}/>
                        </FloatingLabel>

                        <FloatingLabel label={t("send-otp-secret-by-email.phone-number")} className="text-secondary mb-3">
                            <Form.Control type="tel" value={phoneNumber} placeholder={t("send-otp-secret-by-email.phone-number")}
                                          onChange={e => setPhoneNumber(e.target.value)}/>
                        </FloatingLabel>

                        <FloatingLabel label={t("send-otp-secret-by-email.password")} className="text-secondary mb-3">
                            <Form.Control type="password" value={password} placeholder={t("send-otp-secret-by-email.password")}
                                          autoComplete="new-password"
                                          onChange={e => setPassword(e.target.value)}/>
                        </FloatingLabel>
                    </main>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="success"
                        onClick={send}
                        disabled={StringUtils.isBlank(from) || StringUtils.isBlank(recipient) ||
                            (StringUtils.isBlank(phoneNumber) && StringUtils.isBlank(password))}>
                    {sending && <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        aria-hidden="true"
                        className="me-1"
                    />}
                    {sending ? t("common.sending") : t("common.send")}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
